import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { lazy, Suspense, useCallback, useMemo } from 'react';

import { useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';

import styles from '../../styles/documents/survey-document.module.css';

const SurveyComponents = {
    Default: lazy(() => import('../../interactives/survey/Survey')),
};

interface SurveyDocumentProps {
    type?: 'survey' | 'quiz';
    slug?: string;
    title?: string;
    blocks?: Block[];
    form?: Form;
    image?: Image;
    images?: DocumentImages;
    component?: keyof typeof SurveyComponents;
    placeholder?: boolean;
    className: 'string';
    onEnter?: () => void;
}

function SurveyDocument({
    type = 'survey',
    slug = null,
    title = null,
    blocks = null,
    form = null,
    image = null,
    images = null,
    component = null,
    placeholder = false,
    className = null,
    onEnter = null,
}: SurveyDocumentProps) {
    const SurveyComponent = getComponentFromName(SurveyComponents, component, 'default');
    const { header: headerImage = null } = images || {};
    const documentUrl = useDocumentUrlGenerator();
    const intro = useMemo(
        () => ({
            id: 'intro',
            type: 'intro',
            title,
            blocks,
            image: headerImage || image,
        }),
        [title, blocks, headerImage, image],
    );
    const onStart = useCallback(() => {
        if (onEnter !== null) {
            onEnter();
        }
    }, [onEnter]);

    return (
        <div className={classNames([styles.container, className])}>
            <Suspense fallback={<div>Loading...</div>}>
                <SurveyComponent
                    intro={intro}
                    form={form}
                    placeholder={placeholder}
                    className={styles.survey}
                    basePath={
                        slug !== null
                            ? documentUrl({
                                  type,
                                  slug,
                              })
                            : null
                    }
                    onStart={onStart}
                />
            </Suspense>
        </div>
    );
}

export default SurveyDocument;
